<script setup>
import EnvelopeIcon from '@iconify-vue/ph/envelope-fill';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DialogModal from '@js/components/dialogs/DialogModal.vue';
import TextareaField from '@js/components/fields/TextareaField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { toastError, toastSuccess, toastWarning } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { object, string } from 'yup';

const QUICK_EMAILS = [
    'anthony@apikom.fr',
    'valere@apikom.fr',
    'sophie@apikom.fr',
    'benoit@apikom.fr',
    'nicolas@apikom.fr',
    'theo@apikom.fr',
    'anthony.courquin@gmail.com',
    'valere.fagot@gmail.com',
    'smassemin@gmail.com',
];

const { send } = defineProps({
    /**
     * Sends the test to one address and returns the request, called once per address.
     */
    send: {
        type: Function,
        required: true,
    },
});

const isOpen = ref(false);

const isSubmitting = ref(false);

const form = reactive({
    emails: '',
});

function parseEmails(raw) {
    if (!raw) return [];

    return [
        ...new Set(
            raw
                .split(/[,\n]/)
                .map((email) => email.trim())
                .filter((email) => email.length > 0),
        ),
    ];
}

const parsedEmails = computed(() => parseEmails(form.emails));

const emailSchema = string().email();

const validationSchema = object({
    emails: string()
        .required('Le champ email est requis')
        .test(
            'valid-emails',
            'Une ou plusieurs adresses email sont invalides',
            (value) => {
                const emails = parseEmails(value);
                if (emails.length === 0) return false;
                return emails.every((email) => emailSchema.isValidSync(email));
            },
        ),
});

function toggleEmail(email) {
    const current = parsedEmails.value;

    if (current.includes(email)) {
        form.emails = current.filter((item) => item !== email).join('\n');

        return;
    }

    form.emails = [...current, email].join('\n');
}

function isEmailSelected(email) {
    return parsedEmails.value.includes(email);
}

async function sendTestEmail() {
    isSubmitting.value = true;

    const emails = parsedEmails.value;

    const results = await Promise.allSettled(
        emails.map((email) => send(email)),
    );

    isSubmitting.value = false;

    const failed = results.filter((result) => result.status === 'rejected');
    const succeeded = results.length - failed.length;

    if (failed.length === 0) {
        toastSuccess(
            succeeded === 1
                ? 'Email de test envoyé avec succès'
                : `${succeeded} emails de test envoyés avec succès`,
        );
        isOpen.value = false;
        form.emails = '';

        return;
    }

    if (succeeded === 0) {
        toastError(
            "Une erreur est survenue lors de l'envoi de l'email de test.",
        );

        return;
    }

    toastWarning(`${succeeded} email(s) envoyé(s), ${failed.length} en échec.`);
}
</script>

<template>
    <DialogModal v-model="isOpen" size="small">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Envoyer un email de test </template>

        <template #full-content>
            <FormRoot
                :submit="sendTestEmail"
                :validation-schema="validationSchema"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <div class="mb-4 flex flex-wrap gap-2">
                            <ActionButton
                                v-for="email in QUICK_EMAILS"
                                :key="email"
                                :callback="() => toggleEmail(email)"
                                size="small"
                                :type="
                                    isEmailSelected(email)
                                        ? 'primary'
                                        : 'secondary'
                                "
                                :left-icon="EnvelopeIcon"
                            >
                                {{ email }}
                            </ActionButton>
                        </div>

                        <TextareaField
                            v-model="form.emails"
                            label="Emails"
                            field-name="emails"
                            field-id="emails"
                            placeholder="Une adresse par ligne ou séparées par des virgules"
                            legend="Une adresse par ligne ou séparées par des virgules"
                            :rows="4"
                            required
                        />
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :isSubmitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                    submit-text="Envoyer"
                    submitting-text="Envoi en cours..."
                />
            </FormRoot>
        </template>
    </DialogModal>
</template>
